import { useMetaDataContext } from '../state-management/contexts/MetaDataContext'
import { formatFundCategoryLabel, formatFundNatureLabel } from '../utils/fundKindLabel'
import { convertEpochToDateWithOrdinalDay, fixedDigitsAndFormatNumberWithCurrency } from '../utils/utils'
import { mfLogoBaseUrl } from '../env'
import { useEffect, useMemo, useRef, useState } from 'react'
import { PowerPerformanceRiveStateType, PowerPerformanceRiveStates } from '../types/mutual-fund-detail.types'
import { PowerPerformanceRiveSkeleton } from './skeletons/PowerPerformanceRiveSkeleton'
import { lightImpactVibrate, postCTA, trackEvent } from '../utils/appBridge'
import { EventNames } from '../types/misc.types'
import { useViewLeaderboard } from '../hooks/useViewLeaderboard'
import { LeaderboardSheetWrapper } from './peers/LeaderboardSheetWrapper'
import RiveComponent, { RiveFit } from './RiveComponent'

export default function MFHeader() {
  const { data, powerPerformanceRiveState, handleIsPowerRiveReady, powerRank, isUserElite, isin } = useMetaDataContext()
  const [powerState, setPowerState] = useState<`${PowerPerformanceRiveStates}`>('unrated')
  const [isPowerStateRiveLoading, setIsPowerStateRiveLoading] = useState(true)
  const { openBottomSheet, closeBottomSheet, isSheetOpen, peerData, getRank, fundCategory, alternateISIN } =
    useViewLeaderboard()

  const nameRef = useRef(null)

  const isPortfolioInsightsLocked = data?.portfolioInsightsLockedInfo?.status === 'LOCKED'
  const showEliteRank = isUserElite && !isPortfolioInsightsLocked

  const isSadRiveState = () => {
    return (
      [PowerPerformanceRiveStates.UNABLE_TO_LOAD, PowerPerformanceRiveStates.UNRATED] as PowerPerformanceRiveStateType[]
    )?.includes(powerPerformanceRiveState)
  }

  const getPowerState = (): `${PowerPerformanceRiveStates}` => {
    if (!powerPerformanceRiveState) return PowerPerformanceRiveStates.UNABLE_TO_LOAD
    if (isSadRiveState()) return powerPerformanceRiveState
    const eliteString = showEliteRank ? 'elite' : 'non-elite'
    return `${eliteString}` as `${PowerPerformanceRiveStates}`
  }

  useEffect(() => {
    if (powerPerformanceRiveState) {
      const powerStateCopy = getPowerState()
      const timeOutMs = !isSadRiveState() && showEliteRank ? 200 : 100
      setTimeout(() => {
        setIsPowerStateRiveLoading(false)
      }, timeOutMs)
      setPowerState(() => powerStateCopy)
    }
  }, [powerPerformanceRiveState, powerRank, isUserElite, isPortfolioInsightsLocked])

  useEffect(() => {
    if (!isPowerStateRiveLoading) handleIsPowerRiveReady(true)
  }, [isPowerStateRiveLoading])

  const navLine = useMemo(() => {
    return `${fixedDigitsAndFormatNumberWithCurrency(data?.fundDetails?.navToday ?? 0, 2)} NAV 
    (${convertEpochToDateWithOrdinalDay(data?.fundDetails?.lastSyncDate ?? 0, '%b')})`
  }, [data?.fundDetails?.navToday, data?.fundDetails?.lastSyncDate])

  const isRankInteractive =
    powerState !== PowerPerformanceRiveStates.UNRATED &&
    powerState !== PowerPerformanceRiveStates.UNABLE_TO_LOAD

  const powerRankLabel = useMemo(() => {
    if (showEliteRank && powerRank?.powerRank > 0) {
      return `Power rank ${powerRank.powerRank} of ${powerRank.outOf}, double tap to view leaderboard`
    }
    if (isRankInteractive) {
      return 'Power rank, double tap to view details'
    }
    return 'Power rank'
  }, [showEliteRank, powerRank, isRankInteractive])

  return (
    <div key='big' id='fullHeaderBig' className='flex flex-col pb-5 w-full'>
      <div id='mutualFundHeader' className='flex justify-between items-center gap-[20px] w-full'>
        <div id='mutualFundDetails' className='flex flex-col w-[150px] flex-1'>
          <div id='mutualFundLogoWrapper' className='pb-[12px]'>
            <div className='rounded-[12px] border border-[#EBF2F2] w-max h-max overflow-hidden'>
              <img
                id='mutualFundLogo'
                src={`${mfLogoBaseUrl}${data?.fundLogo}`}
                alt=''
                aria-hidden='true'
                className='h-10 w-10'
              />
            </div>
          </div>
          <h1
            ref={nameRef}
            id='mutualFundName'
            role='heading'
            aria-level={1}
            className='text-[16px] font-[500] text-[#28393E] pb-[4px] leading-[130%] break-words max-w-full m-0'
          >
            {data?.fundDetails?.fundName}
          </h1>
          <div id='mutualFundKindWrapper' className='flex flex-wrap gap-[4px] text-13 text-[#6A6E6E] font-[400] pb-[6px]'>
            {[data?.fundDetails?.fundNature, data?.fundDetails?.category, data?.fundDetails?.purchaseMode]?.map(
              (datum, idx) => {
                const isCategory = idx === 1 // category is the second item (index 1)
                const formatted = isCategory ? formatFundCategoryLabel(datum) : formatFundNatureLabel(datum)
                return (
                  <div
                    id='mutualFundKind'
                    key={`${datum}${idx.toString()}`}
                    className='flex items-center [&:last-child>.separator]:hidden'
                  >
                    {formatted}
                    <span aria-hidden='true' className='pl-[4px] pt-[1px] separator'>
                      <svg xmlns='http://www.w3.org/2000/svg' width='4' height='5' viewBox='0 0 4 5' fill='none'>
                        <path d='M2 0.5L0 2.5L2 4.5L4 2.5L2 0.5Z' fill='#6A6E6E' />
                      </svg>
                    </span>
                  </div>
                )
              }
            )}
          </div>
          <div className='text-[13px] text-[#6A6E6E] font-[400] pt-2'>
            {navLine}
          </div>
        </div>
        <button
          type='button'
          disabled={!isRankInteractive}
          aria-label={powerRankLabel}
          onClick={() => {
            if (!isRankInteractive) return
            lightImpactVibrate()
            trackEvent({
              eventName: EventNames.UserClicked,
              payload: {
                screenType: 'MF_L2',
                responseName: 'fund_badge',
                fundName: isin
              }
            })
            if (showEliteRank) openBottomSheet()
            else if (data?.eliteIconCta) postCTA(JSON.stringify(data?.eliteIconCta))
          }}
          id='mutualFundRankWrapper'
          className='h-[104px] w-[104px] relative flex justify-center items-center z-0 p-0 border-none bg-transparent cursor-pointer disabled:cursor-default'
        >
          {isPowerStateRiveLoading ? (
            <PowerPerformanceRiveSkeleton />
          ) : (
            <div
              aria-hidden='true'
              className={`h-full w-full animate-fade-250 ${isPowerStateRiveLoading ? 'opacity-0' : 'opacity-1'} `}
            >
              <RiveComponent
                src={`/rives/power_performance.riv`}
                fit={RiveFit.FILL}
                stateMachines={powerState}
                textRunValues={{
                  rank:
                    powerState === PowerPerformanceRiveStates.ELITE && powerRank?.powerRank > 0
                      ? `${powerRank.powerRank}`
                      : '',
                  'out-of':
                    powerState === PowerPerformanceRiveStates.ELITE && powerRank?.powerRank > 0
                      ? `of ${powerRank.outOf}`
                      : ''
                }}
              />
            </div>
          )}
        </button>
      </div>
      <LeaderboardSheetWrapper
        fundCategory={fundCategory}
        closeBottomSheet={closeBottomSheet}
        isSheetOpen={isSheetOpen}
        peerData={peerData}
        getRank={getRank}
        alternateISIN={alternateISIN}
      />
    </div>
  )
}
